import type {RowData} from "@tanstack/react-table";
import {createContext, useContext, useEffect, useState} from "react";
import type {ServerTableContextValue, ServerTableProviderProps} from "./types";

const ServerTableContext = createContext<ServerTableContextValue<any> | null>(null);

/** Publishes one completed table controller and its shared fullscreen state. */
export const ServerTableProvider = <TData extends RowData>({
    controller,
    children
}: ServerTableProviderProps<TData>) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const contextValue: ServerTableContextValue<TData> = {
        ...controller,
        tableId: controller.table.options.meta.tableId,
        isFullScreen,
        setIsFullScreen
    };

    useEffect(() => {
        if (!isFullScreen) return;

        const exitOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsFullScreen(false);
        };

        document.addEventListener('keydown', exitOnEscape);
        return () => document.removeEventListener('keydown', exitOnEscape);
    }, [isFullScreen]);

    return (
        <ServerTableContext.Provider value={contextValue}>
            {children}
        </ServerTableContext.Provider>
    );
};

export const useServerTableContext = <TData extends RowData = RowData>() => {
    const controller = useContext(ServerTableContext);

    if (!controller) {
        throw new Error('useServerTableContext must be used within a ServerTableProvider.');
    }

    // React context is not generic per provider instance; restore the row type at this boundary.
    return controller as ServerTableContextValue<TData>;
};
